import React from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import {
    Alert,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from '@mui/material';
import Personnummer from 'personnummer';
import { AuthMethod } from 'models/user';
import {
    adminAuthMethodOptions,
    isAuthMethodEditable,
    isValidPersonalIdentityNumber,
    requiresPersonalIdentityNumber,
} from 'utils/admin-auth-methods';

export type AuthMethodFormValues = {
    authMethod?: AuthMethod;
    syncid?: string;
    enableUserSync?: boolean;
    personalIdentityNumber?: string;
};

type AuthMethodFieldsProps<Values extends AuthMethodFormValues> = {
    formik: FormikProps<Values>;
    /** The method the user has now. Undefined when creating a user. */
    currentMethod?: AuthMethod;
};

/**
 * Adds the login-method errors to a form's validate() result. Nothing to check when
 * the method is managed elsewhere: the form then sends it back unchanged.
 */
export const validateAuthMethodFields = (
    values: AuthMethodFormValues,
    translate: TFunction,
    errors: Record<string, string>,
    { currentMethod }: { currentMethod?: AuthMethod } = {},
): void => {
    if (!isAuthMethodEditable(currentMethod)) {
        return;
    }
    if (!values.authMethod) {
        errors.authMethod = translate('users:VALIDATION:AUTH_METHOD');
    }
    if (values.authMethod === AuthMethod.SITHS && !values.syncid) {
        errors.syncid = translate('users:VALIDATION:SITHS_ID');
    }
    if (
        requiresPersonalIdentityNumber(values.authMethod) &&
        !isValidPersonalIdentityNumber(values.authMethod, values.personalIdentityNumber)
    ) {
        errors.personalIdentityNumber = translate('users:VALIDATION:PERSONAL_IDENTITY_NUMBER');
    }
};

const formatPersonalIdentityNumber = (method: AuthMethod | undefined, value: string) =>
    method === AuthMethod.BANKID_SE ? new Personnummer(value).format(true) : value.trim().toUpperCase();

/** The login-method part of the addDentist/addTechnician/modify* variables. */
export const authMethodVariables = (values: AuthMethodFormValues) => ({
    authMethod: values.authMethod,
    syncid: values.authMethod === AuthMethod.SITHS ? values.syncid?.trim() : null,
    enableUserSync: values.enableUserSync,
    personalIdentityNumber:
        requiresPersonalIdentityNumber(values.authMethod) && values.personalIdentityNumber
            ? formatPersonalIdentityNumber(values.authMethod, values.personalIdentityNumber)
            : null,
});

/**
 * The login-method fields shared by the dentist, lab user and user forms: the method
 * selector, the SITHS id and user sync for SITHS, and the personal identity number a
 * number-bound e-ID is matched on at the user's first login.
 */
const AuthMethodFields = <Values extends AuthMethodFormValues>({
    formik,
    currentMethod,
}: AuthMethodFieldsProps<Values>) => {
    const { t: translate } = useTranslation(['users', 'common']);
    const selectedMethod = formik.values.authMethod;
    const isEditable = isAuthMethodEditable(currentMethod);
    const fieldError = (name: keyof AuthMethodFormValues): string | undefined =>
        formik.touched[name] ? (formik.errors[name] as string | undefined) : undefined;
    const methodError = fieldError('authMethod');
    const pinError = fieldError('personalIdentityNumber');
    const syncidError = fieldError('syncid');

    const handleAuthMethodChange = (event: SelectChangeEvent) => {
        const value = event.target.value as AuthMethod;
        if (value !== AuthMethod.SITHS) {
            formik.setFieldValue('syncid', '');
        }
        formik.setFieldValue('authMethod', value);
    };

    return (
        <>
            <Grid item xs={6}>
                <FormControl margin="dense" fullWidth error={Boolean(methodError)} required>
                    <InputLabel id="auth-method-select-label">{translate('users:LOGIN_TYPE')}</InputLabel>
                    <Select
                        labelId="auth-method-select-label"
                        id="auth-method-select"
                        label={translate('users:LOGIN_TYPE')}
                        required
                        error={Boolean(methodError)}
                        onChange={handleAuthMethodChange}
                        disabled={!isEditable}
                        onBlur={formik.getFieldProps('authMethod').onBlur}
                        name="authMethod"
                        value={selectedMethod ?? ''}
                        renderValue={(method) => translate(`users:AUTH_METHOD:${method}`)}
                        data-test="user-form.auth-method"
                    >
                        {adminAuthMethodOptions(currentMethod).map(({ method, disabled }) => (
                            <MenuItem key={method} value={method} disabled={disabled}>
                                <ListItemText
                                    primary={translate(`users:AUTH_METHOD:${method}`)}
                                    secondary={
                                        disabled ? translate('users:AUTH_METHOD_NOT_PRE_REGISTRABLE') : undefined
                                    }
                                />
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText error={Boolean(methodError)}>
                        {isEditable ? methodError : translate('users:AUTH_METHOD_MANAGED_ELSEWHERE')}
                    </FormHelperText>
                </FormControl>
            </Grid>
            {selectedMethod === AuthMethod.SITHS ? (
                <>
                    <Grid item xs={6}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="syncid"
                                label={translate('users:SITHS_ID')}
                                required
                                helperText={syncidError}
                                error={Boolean(syncidError)}
                                {...formik.getFieldProps('syncid')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl margin="dense" fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(formik.values.enableUserSync)}
                                        onChange={(event) =>
                                            formik.setFieldValue('enableUserSync', event.target.checked)
                                        }
                                        name="enableUserSync"
                                        color="primary"
                                    />
                                }
                                label={translate('users:ENABLE_USER_SYNC')}
                            />
                        </FormControl>
                    </Grid>
                </>
            ) : undefined}
            {requiresPersonalIdentityNumber(selectedMethod) && isEditable ? (
                <>
                    <Grid item xs={6}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="personal-identity-number"
                                label={translate(
                                    selectedMethod === AuthMethod.FTN_FI
                                        ? 'users:FINNISH_IDENTITY_CODE'
                                        : 'common:PERSONAL_IDENTITY_NUMBER',
                                )}
                                required
                                helperText={pinError}
                                error={Boolean(pinError)}
                                {...formik.getFieldProps('personalIdentityNumber')}
                                data-test="user-form.personal-identity-number"
                            />
                        </FormControl>
                    </Grid>
                    {selectedMethod !== currentMethod ? (
                        <Grid item xs={12}>
                            <Alert severity="info">{translate('users:AUTH_METHOD_EID_ONLY_INFO')}</Alert>
                        </Grid>
                    ) : undefined}
                </>
            ) : undefined}
        </>
    );
};

export default AuthMethodFields;
